import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import { LockRounded, VerifiedUserRounded } from '@mui/icons-material';
import {
  Box,
  Stack,
  Alert,
  TextField,
  Typography,
} from '@mui/material';


export function useSignatureOtp({
  active,
  sessionId,
  expiresAt,
  channel = 'sms',
  destinationMasked,
  companyId,
  documentCount = 1,
  onSuccess,
  executeSignature,
}) {
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (!active) {
      setOtp('');
      setError(null);
      setTimeRemaining(null);
    }
  }, [active]);

  useEffect(() => {
    if (active && expiresAt) {
      const interval = setInterval(() => {
        const now = dayjs();
        const expires = dayjs(expiresAt);
        const diff = expires.diff(now, 'second');

        if (diff > 0) {
          const minutes = Math.floor(diff / 60);
          const seconds = diff % 60;
          setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setTimeRemaining('Scaduto');
          setError('La sessione è scaduta. Inizia una nuova sessione di firma.');
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [active, expiresAt]);

  const handleSign = async () => {
    if (!otp) {
      setError('Inserisci il codice OTP');
      return;
    }

    if (otp.length !== 6) {
      setError('L\'OTP deve essere di 6 cifre');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await executeSignature(companyId, sessionId, otp);

      if (res.data.success) {
        enqueueSnackbar(
          documentCount === 1
            ? 'Documento firmato con successo!'
            : `${documentCount} documenti firmati con successo!`,
          { variant: 'success' },
        );
        onSuccess();
      }
    } catch (e) {
      console.error('Error executing signature:', e);
      const errorMessage = e.response?.data?.message || 'Errore durante la firma dei documenti';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isExpired = timeRemaining === 'Scaduto';
  const signLabel = documentCount === 1 ? 'Firma Documento' : `Firma ${documentCount} Documenti`;

  return {
    otp,
    setOtp,
    loading,
    error,
    timeRemaining,
    isExpired,
    handleSign,
    signLabel,
    signDisabled: isExpired || !otp || otp.length !== 6,
    channel,
    destinationMasked,
  };
}

export function SignatureOtpPanelContent({ otpState }) {
  const {
    otp,
    setOtp,
    loading,
    error,
    timeRemaining,
    isExpired,
    channel,
    destinationMasked,
  } = otpState;
  const otpDelivery = useMemo(() => {
    if (channel === 'email') {
      return {
        instruction: 'Inserisci il codice OTP ricevuto via email aziendale',
        helper: destinationMasked
          ? `Codice a 6 cifre inviato a ${destinationMasked}`
          : 'Codice a 6 cifre ricevuto sulla tua email aziendale',
      };
    }

    return {
      instruction: 'Inserisci il codice OTP ricevuto via SMS/WhatsApp',
      helper: destinationMasked
        ? `Codice a 6 cifre inviato a ${destinationMasked}`
        : 'Codice a 6 cifre ricevuto sul tuo numero di telefono',
    };
  }, [channel, destinationMasked]);

  return (
    <Stack spacing={2.5} sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <LockRounded color="primary" />
        <Typography variant="subtitle1" fontWeight={600}>
          Esegui Firma
        </Typography>
      </Stack>

      {timeRemaining && (
        <Alert severity={isExpired ? 'error' : 'info'} sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {isExpired ? 'Sessione scaduta' : `Tempo rimanente: ${timeRemaining}`}
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {otpDelivery.instruction}
        </Typography>
        <TextField
          fullWidth
          label="OTP"
          value={otp}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').substring(0, 6);
            setOtp(value);
          }}
          disabled={loading || isExpired}
          inputProps={{ maxLength: 6 }}
          helperText={otpDelivery.helper}
        />
      </Box>

      <Alert severity="warning" sx={{ py: 0.5 }}>
        <Typography variant="body2">
          Assicurati di avere l&apos;OTP corretto. Dopo 3 tentativi falliti, la sessione verrà bloccata.
        </Typography>
      </Alert>
    </Stack>
  );
}

SignatureOtpPanelContent.propTypes = {
  otpState: PropTypes.object.isRequired,
};

export function SignatureOtpPanelActions({ otpState, onCancel }) {
  const { loading, handleSign, signLabel, signDisabled } = otpState;

  return (
    <>
      <LoadingButton
        variant="outlined"
        color="inherit"
        onClick={onCancel}
        disabled={loading}
      >
        Indietro
      </LoadingButton>
      <LoadingButton
        variant="contained"
        loading={loading}
        onClick={handleSign}
        disabled={signDisabled}
        startIcon={<VerifiedUserRounded />}
      >
        {signLabel}
      </LoadingButton>
    </>
  );
}

SignatureOtpPanelActions.propTypes = {
  otpState: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default function SignatureOtpPanel(props) {
  const otpState = useSignatureOtp(props);
  const { onCancel } = props;

  return (
    <>
      <SignatureOtpPanelContent otpState={otpState} />
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 2 }}>
        <SignatureOtpPanelActions otpState={otpState} onCancel={onCancel} />
      </Stack>
    </>
  );
}

SignatureOtpPanel.propTypes = {
  active: PropTypes.bool.isRequired,
  sessionId: PropTypes.string.isRequired,
  expiresAt: PropTypes.string.isRequired,
  channel: PropTypes.oneOf(['sms', 'email']),
  destinationMasked: PropTypes.string,
  companyId: PropTypes.number.isRequired,
  documentCount: PropTypes.number,
  executeSignature: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
